import { useEffect, useState } from 'react';
import { Avatar, Drawer, Modal, Spin, Tag, Empty, List, Card } from 'antd';
import { NavLink } from 'react-router-dom';
import { ClockCircleOutlined, FolderOpenOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { PersonnelService } from '../../services/PersonnelService';
import { catchError } from '../../services/DaoService';
import PropTypes from 'prop-types';
import styles from './PersonnelQuickView.module.scss';

export default function PersonnelQuickView({ personnel, mode = 'aside', onClose }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!personnel) return;
    setLoading(true);
    setDetail(null);
    PersonnelService.getInstance()
      .monEquipeDetail(personnel.uid)
      .then(r => setDetail(r.data))
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [personnel]);

  const bodyContent = (
    <div className={styles.container}>
      {personnel && (
        <Card className={styles.headerCard} bordered={false}>
          <div className={styles.header}>
            <Avatar size={64} src={personnel.avatar_link} className={styles.avatar}>
              {personnel.prenoms?.[0]}
            </Avatar>
            <div className={styles.identity}>
              <div className={styles.name}>{personnel.name}</div>
              <div className={styles.metaRow}>
                {personnel.departement?.nom && <Tag className={styles.metaTag}>{personnel.departement.nom}</Tag>}
                <Tag color={personnel.enabled ? 'green' : 'default'} className={styles.stateTag}>
                  {personnel.enabled ? 'Actif' : 'Inactif'}
                </Tag>
              </div>
              <div className={styles.contactRow}>
                <span className={styles.contactChip}><MailOutlined /> {personnel.email}</span>
                <span className={styles.contactChip}><PhoneOutlined /> {personnel.telephone || 'Non renseigné'}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Spin spinning={loading}>
        {detail && (
          <div className={styles.sections}>
            <Card className={styles.sectionCard} bordered={false} title={<span className={styles.sectionTitle}><ClockCircleOutlined /> Tâches en cours</span>}>
              {detail.taches.length === 0 ? (
                <Empty description="Aucune tâche en cours" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  size="small"
                  dataSource={detail.taches}
                  className={styles.list}
                  renderItem={(t) => (
                    <List.Item className={styles.listItem}>
                      <div className={styles.listMain}>
                        <NavLink to={t.lien} onClick={onClose} className={styles.itemLink}>{t.nom}</NavLink>
                        {t.dossier && <span className={styles.itemSub}>{t.dossier}</span>}
                      </div>
                      {t.en_retard && <Tag color="red" className={styles.alertTag}>En retard</Tag>}
                    </List.Item>
                  )}
                  />
              )}
            </Card>

            <Card className={styles.sectionCard} bordered={false} title={<span className={styles.sectionTitle}><FolderOpenOutlined /> Dossiers responsable</span>}>
              {!detail.a_un_compte_applicatif ? (
                <Empty description="Ce collaborateur n'a pas de compte applicatif" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : detail.dossiers.length === 0 ? (
                <Empty description="Aucun dossier en cours" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  size="small"
                  dataSource={detail.dossiers}
                  className={styles.list}
                  renderItem={(a) => (
                    <List.Item className={styles.listItem}>
                      <div className={styles.listMain}>
                        <NavLink to={a.lien} onClick={onClose} className={styles.itemLink}>{a.nom}</NavLink>
                      </div>
                      {a.en_retard && <Tag color="red" className={styles.alertTag}>En retard</Tag>}
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </div>
        )}
      </Spin>
    </div>
  );

  return mode === 'modal' ? (
    <Modal open={!!personnel} onCancel={onClose} footer={null} title={null} width={Math.min(560, window.innerWidth - 16)} className={styles.modal}>
      {bodyContent}
    </Modal>
  ) : (
    <Drawer open={!!personnel} onClose={onClose} placement="right" width={Math.min(480, window.innerWidth - 16)} title={null} className={styles.drawer}>
      {bodyContent}
    </Drawer>
  );
}

PersonnelQuickView.propTypes = {
  personnel: PropTypes.object,
  mode: PropTypes.oneOf(['aside', 'modal']),
  onClose: PropTypes.func.isRequired,
};
